import { useState, type FormEvent } from 'react'
import { FileText } from 'lucide-react'
import { Button } from '../../../modules/core/index.js'
import type { GenerateSummaryInput } from '../types.js'
import { FORM_INPUT_CLASS } from '../../../modules/core/index.js'

type SpanChoice = 'year_to_date' | 'last_year' | 'custom'

const NOTE_MAX_LENGTH = 500
const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/

interface GenerateSummaryFormProps {
  isGenerating: boolean
  error: string | null
  onGenerate: (input: GenerateSummaryInput) => void
  onCancel: () => void
}

/** The one choice a summary takes: which months, and an optional context note. */
export function GenerateSummaryForm({ isGenerating, error, onGenerate, onCancel }: GenerateSummaryFormProps) {
  const [choice, setChoice] = useState<SpanChoice>('year_to_date')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [note, setNote] = useState('')
  const lastYear = new Date().getFullYear() - 1
  const customValid = MONTH_PATTERN.test(from) && MONTH_PATTERN.test(to) && from <= to
  const canSubmit = !isGenerating && (choice !== 'custom' || customValid)

  function submit(event: FormEvent): void {
    event.preventDefault()
    if (!canSubmit) {
      return
    }
    const trimmed = note.trim()
    onGenerate({
      ...(choice === 'last_year' ? { span: { from: `${lastYear}-01`, to: `${lastYear}-12` } } : choice === 'custom' ? { span: { from, to } } : {}),
      ...(trimmed === '' ? {} : { note: trimmed }),
    })
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 border border-marine/15 bg-white p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-[11px] font-bold text-ink-mid">
          Months
          <select className={FORM_INPUT_CLASS} value={choice} onChange={(event) => setChoice(event.target.value as SpanChoice)}>
            <option value="year_to_date">This year to date (closed months)</option>
            <option value="last_year">{lastYear}, the whole year</option>
            <option value="custom">Choose the months</option>
          </select>
        </label>
        {choice === 'custom' && (
          <div className="flex items-end gap-2">
            <label className="flex flex-1 flex-col gap-1 text-[11px] font-bold text-ink-mid">
              From
              <input type="month" className={FORM_INPUT_CLASS} value={from} onChange={(event) => setFrom(event.target.value)} />
            </label>
            <label className="flex flex-1 flex-col gap-1 text-[11px] font-bold text-ink-mid">
              To
              <input type="month" className={FORM_INPUT_CLASS} value={to} onChange={(event) => setTo(event.target.value)} />
            </label>
          </div>
        )}
      </div>
      <label className="flex flex-col gap-1 text-[11px] font-bold text-ink-mid">
        Context note (optional)
        <input className={FORM_INPUT_CLASS} maxLength={NOTE_MAX_LENGTH} placeholder="Six weeks of leave in March, not counted." value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
      {choice === 'custom' && !customValid && (from !== '' || to !== '') && <p className="text-[11px] text-coral">Both months are needed, the first no later than the second, at most 24 months apart.</p>}
      {error && (
        <p role="alert" className="text-[11px] text-coral">
          {error}
        </p>
      )}
      <div className="flex items-center gap-3">
        <Button icon={FileText} type="submit" disabled={!canSubmit}>
          {isGenerating ? 'Generating…' : 'Generate summary'}
        </Button>
        <button type="button" onClick={onCancel} className="text-xs font-bold text-ink-mid hover:text-ink">
          Cancel
        </button>
      </div>
    </form>
  )
}
